import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import GameCard from '@/components/base/GameCard';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryCard from '@/components/base/CategoryCard';
import { categories, articles, featuredGames, hotRankings, latestNews } from '@/mocks/games';

const rankIcons: Record<string, string> = {
  up: 'ri-arrow-up-line text-accent-500',
  down: 'ri-arrow-down-line text-primary-500',
  stable: 'ri-subtract-line text-foreground-400',
};

export default function Home() {
  const seoData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '工口游戏推荐与攻略大全',
    url: 'https://eroge-guide.com/',
    description: '本页汇集热门工口游戏资讯，涵盖工口游戏类型介绍、玩法攻略、新作推荐及平台下载指引。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://eroge-guide.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOHead
        title="工口游戏推荐与攻略大全 - 热门工口游戏资讯平台"
        description="本页汇集热门工口游戏资讯，涵盖工口游戏类型介绍、玩法攻略、新作推荐及平台下载指引，帮助玩家快速找到心仪的工口游戏。"
        keywords="工口游戏,工口游戏推荐,工口游戏下载,工口游戏攻略,工口游戏排行榜,成人游戏,18禁游戏"
        canonicalUrl="https://eroge-guide.com/"
        ogType="website"
        schemaJson={seoData}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20fantasy%20game%20art%20atmosphere%20with%20mysterious%20silhouette%20and%20warm%20crimson%20glowing%20accents%2C%20dramatic%20cinematic%20light%20rays%2C%20epic%20game%20illustration%20style%2C%20rich%20dark%20colors%20with%20golden%20highlights%2C%20no%20text%20no%20logo%20clean%20background&width=1800&height=1000&seq=hero-bg-main&orientation=landscape"
            alt="工口游戏推荐与攻略大全"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-5 leading-tight">
              工口游戏推荐与攻略大全
            </h1>
            <p className="text-base md:text-lg text-background-200/90 mb-8 max-w-2xl mx-auto leading-relaxed">
              汇集热门工口游戏资讯，涵盖类型介绍、玩法攻略、新作推荐及平台下载指引，帮助玩家快速找到心仪的工口游戏，了解各作品剧情与系统机制。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/rpg"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-compass-3-line"></i>
                探索游戏分类
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/15 hover:bg-background-50/25 text-background-50 font-medium rounded-full backdrop-blur-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line"></i>
                关于我们
              </Link>
            </div>
          </div>
        </section>

        {/* Hot Rankings + Featured Games */}
        <section className="py-14 md:py-18 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Rankings */}
              <div className="lg:col-span-1">
                <div className="flex items-center gap-2 mb-6">
                  <i className="ri-fire-line text-2xl text-primary-500"></i>
                  <h2 className="text-xl font-heading font-bold text-foreground-900">
                    热门排行榜
                  </h2>
                </div>
                <div className="bg-background-50 border border-background-200/60 rounded-lg p-4">
                  {hotRankings.map((item) => (
                    <Link
                      key={item.rank}
                      to={`/article/${getRankSlug(item.rank)}`}
                      className="flex items-center gap-3 py-3 border-b border-background-100 last:border-0 hover:bg-background-50/60 transition-colors cursor-pointer rounded-md px-2 -mx-2"
                    >
                      <span
                        className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                          item.rank <= 3
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-secondary-200 text-secondary-700'
                        }`}
                      >
                        {item.rank}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-800 truncate">
                          {item.title}
                        </p>
                        <span className="text-xs text-foreground-400">{item.category}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-xs font-medium text-foreground-700">{item.rating}</span>
                        <i className={rankIcons[item.trend]}></i>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Featured Games */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <i className="ri-star-line text-2xl text-accent-500"></i>
                    <h2 className="text-xl font-heading font-bold text-foreground-900">
                      编辑精选推荐
                    </h2>
                  </div>
                  <Link
                    to="/category/rpg"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    查看更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featuredGames.map((game) => (
                    <GameCard key={game.slug} {...game} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                游戏分类导览
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto">
                按游戏类型浏览，快速找到你感兴趣的工口游戏品类
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <CategoryCard key={cat.slug} {...cat} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-18 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                <i className="ri-article-line text-2xl text-primary-500"></i>
                <h2 className="text-xl font-heading font-bold text-foreground-900">
                  最新攻略与评测
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {articles.slice(0, 4).map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/rpg"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-primary-300 text-primary-600 hover:bg-primary-50 font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览全部文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest News */}
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-8">
              <i className="ri-newspaper-line text-2xl text-accent-500"></i>
              <h2 className="text-xl font-heading font-bold text-foreground-900">
                最新资讯
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {latestNews.map((news) => (
                <Link
                  key={news.slug}
                  to={`/article/${news.slug}`}
                  className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all cursor-pointer"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 mb-2 inline-block">
                      资讯
                    </span>
                    <h3 className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {news.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                      {news.excerpt}
                    </p>
                    <span className="text-xs text-foreground-400">{news.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 md:py-24 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20dark%20atmospheric%20gaming%20room%20with%20soft%20ambient%20lighting%2C%20rich%20crimson%20and%20golden%20accents%2C%20cozy%20gaming%20setup%2C%20cinematic%20mood%2C%20no%20text%20no%20logo%20clean%20background&width=1800&height=800&seq=cta-bg-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/55"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              发现你的下一款心头好
            </h2>
            <p className="text-background-200/90 mb-8 leading-relaxed">
              无论你是RPG剧情控、AVG文学爱好者，还是SLG策略达人，这里都有适合你的工口游戏推荐。持续更新最新资讯，不错过任何一部佳作。
            </p>
            <Link
              to="/category/doujin"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-rocket-line"></i>
              立即探索
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

function getRankSlug(rank: number): string {
  const slugs: Record<number, string> = {
    1: 'shadow-chronicle',
    2: 'white-clover-poem',
    3: 'stellar-path',
    4: 'avg-masterpiece-ranking',
    5: 'kingdom-revival',
    6: 'crimson-heart',
    7: 'act-must-play-list',
    8: 'puzzle-recommend-2025',
    9: 'abyss-walker',
    10: 'sandbox-open-world',
  };
  return slugs[rank] || 'top-10-rpg-2025';
}